import React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import type { CrudFilter } from "@refinedev/core";
import { List, useDataGrid } from "@refinedev/mui";
import { useNavigate } from "react-router";

import { formatDateTime } from "../creations/kinds";

// One row of GET /api/v2/admin/stories — a creator's own Story Studio story,
// from its `studio_stories` index row (the story itself is a bucket file).
// Never a player's copy or a chat.
type StoryRecord = {
  id: number;
  storyId: string;
  title: string;
  coverUrl: string | null;
  // An adult (immersivex) story: its media is private, so the server hands no cover.
  coverPrivate: boolean;
  storefront: "adult" | "general" | null;
  contentRating: string | null;
  status: string;
  publishedAs: string | null;
  styleId: string | null;
  playerTurns: number;
  level: number | null;
  introSeconds: number | null;
  creatorId: number;
  creatorName: string | null;
  creatorEmail: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lastPlayedAt: string | null;
};

// Mirrors the server's allowlists (admin-stories.controller.ts).
const RATINGS = ["sfw", "mature"] as const;
const STATUSES = ["active", "binned", "set_aside", "closed"] as const;

// How long typing has to pause before the search goes to the server — one
// request per pause, not one per keystroke (as on the users list).
const SEARCH_DEBOUNCE_MS = 300;

const statusColor = (status: string): "default" | "success" | "warning" | "error" => {
  switch (status) {
    case "active":
      return "success";
    case "binned":
      return "warning";
    case "closed":
      return "error";
    default:
      return "default";
  }
};

const muted = <span style={{ opacity: 0.6 }}>-</span>;

export const StoryList = () => {
  // Server-ordered: newest-updated first. Columns are not sortable — the shared
  // data provider sends page/limit only, so client-side sorting would reorder
  // just the current page.
  const { dataGridProps, filters, setFilters } = useDataGrid({});
  const navigate = useNavigate();

  const filterValue = (field: string) =>
    String(filters.find((f) => "field" in f && f.field === field)?.value ?? "");

  // Sets one `field eq value` filter and keeps the others (search, rating and
  // status stand together); an empty value removes it.
  const setFilter = React.useCallback(
    (field: string, value: string) => {
      const others = filters.filter((f): f is CrudFilter => !("field" in f && f.field === field));
      setFilters(value ? [...others, { field, operator: "eq", value }] : others, "replace");
    },
    [filters, setFilters]
  );

  // Forwarded as `?search=`, which the server matches case-insensitively as a
  // substring of the title or the story id, or the creator's email, name or
  // handle (`AdminStoriesRepository`). Local state so typing stays instant;
  // the filter follows it after a pause.
  const activeSearch = filterValue("search");
  const [search, setSearch] = React.useState(activeSearch);

  // A search restored from elsewhere (navigation, with `syncWithLocation`)
  // replaces what the box holds; the one the box itself just sent does not,
  // so typing on past it is never overwritten.
  const sentSearch = React.useRef(activeSearch);
  React.useEffect(() => {
    if (activeSearch !== sentSearch.current) {
      sentSearch.current = activeSearch;
      setSearch(activeSearch);
    }
  }, [activeSearch]);

  React.useEffect(() => {
    const term = search.trim();

    // Only when the term actually changed: re-applying the same filter would
    // still send the grid back to page 1.
    if (term === activeSearch) {
      return;
    }

    const handle = setTimeout(() => {
      sentSearch.current = term;
      setFilter("search", term);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [search, activeSearch, setFilter]);

  const columns = React.useMemo<GridColDef<StoryRecord>[]>(
    () => [
      {
        field: "coverUrl",
        headerName: "Cover",
        minWidth: 80,
        sortable: false,
        display: "flex",
        // The cover on the story's card as of its last save; a story with none
        // yet (nothing painted, no own cover given) is a normal state.
        renderCell: ({ row }) =>
          row.coverUrl ? (
            <Link href={row.coverUrl} target="_blank" rel="noreferrer">
              <Avatar variant="rounded" src={row.coverUrl} alt={row.title} sx={{ width: 48, height: 48 }} />
            </Link>
          ) : row.coverPrivate ? (
            <Tooltip title="An adult story's media is private; no cover is shown here.">
              <Chip size="small" label="private" variant="outlined" />
            </Tooltip>
          ) : (
            muted
          ),
      },
      {
        field: "title",
        headerName: "Story",
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Tooltip title={row.title}>
              <Typography variant="body2" noWrap>
                {row.title || <span style={{ opacity: 0.6 }}>(untitled)</span>}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" noWrap>
              <code>{row.storyId}</code>
              {row.storefront === "adult" ? " · immersivex" : ""}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "contentRating",
        headerName: "Rating",
        minWidth: 100,
        sortable: false,
        display: "flex",
        // What decides, with the creator's own mature setting, whether the
        // story takes a suggestive film of their own.
        renderCell: ({ row }) =>
          row.contentRating ? (
            <Chip
              size="small"
              variant="outlined"
              label={row.contentRating}
              color={row.contentRating === "mature" ? "warning" : "default"}
            />
          ) : (
            muted
          ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 110,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Chip size="small" variant="outlined" label={row.status} color={statusColor(row.status)} />
        ),
      },
      {
        field: "creatorId",
        headerName: "Creator",
        flex: 1,
        minWidth: 190,
        sortable: false,
        display: "flex",
        // The id is the fact and the name is the convenience, so the id is
        // always shown — the join is LEFT and the name can be missing.
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.creatorName ?? <span style={{ opacity: 0.6 }}>(unknown)</span>}
            </Typography>
            <Tooltip title={row.creatorEmail ?? ""}>
              <Typography variant="caption" color="text.secondary" noWrap>
                <code>{row.creatorId}</code>
                {row.creatorEmail ? ` · ${row.creatorEmail}` : ""}
              </Typography>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: "publishedAs",
        headerName: "Published",
        minWidth: 120,
        sortable: false,
        display: "flex",
        // The shelf entry it is published as, while it is.
        renderCell: ({ row }) => (row.publishedAs ? <code>{row.publishedAs}</code> : muted),
      },
      {
        field: "styleId",
        headerName: "Style",
        minWidth: 130,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.styleId ? <Chip size="small" label={row.styleId} variant="outlined" /> : muted,
      },
      {
        field: "playerTurns",
        headerName: "Turns",
        minWidth: 80,
        sortable: false,
        display: "flex",
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        minWidth: 180,
        sortable: false,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "createdAt",
        headerName: "Created",
        minWidth: 180,
        sortable: false,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        minWidth: 70,
        sortable: false,
        display: "flex",
        // The story's index row whole, on the Creations show page.
        renderCell: ({ row }) => (
          <Tooltip title="View record">
            <IconButton size="small" onClick={() => navigate(`/creations/show/story-${row.id}`)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  return (
    <List canCreate={false}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Every creator's own Story Studio stories, newest-updated first — never
          a player's copy or a chat. Read-only: a story's rating and status live
          in its file, and change in the Studio.
        </Typography>
      </Box>
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search title, story id or creator"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Rating"
          value={filterValue("contentRating")}
          onChange={(e) => setFilter("contentRating", e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All ratings</MenuItem>
          {RATINGS.map((rating) => (
            <MenuItem key={rating} value={rating}>
              {rating}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={filterValue("status")}
          onChange={(e) => setFilter("status", e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <DataGrid {...dataGridProps} columns={columns} disableColumnFilter rowHeight={68} />
    </List>
  );
};
